import { Box, Icon, Theme, Tooltip, Typography } from "@outschool/backpack";
import { ActivityStandard } from "@outschool/business-rules";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import {
  faBooks,
  faCirclePlay,
  faGraduationCap,
  faQuestionCircle,
  faRectangleList,
  faSignalStream,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import React from "react";

import {
  getCurriculumValue,
  getStandardValue,
} from "../subcomponents/ActivityAttributes";
import HeadingMarker from "../subcomponents/HeadingMarker";
import { YellowBlobIcon } from "../subcomponents/IconWithBackground";
import ShowMoreModal from "../subcomponents/ShowMoreModal";

interface ActivityWithSyllabus
  extends Omit<ClassDetailsQuery_ActivityFragment, "syllabus"> {
  syllabus: NonNullable<ClassDetailsQuery_ActivityFragment["syllabus"]>;
}

export default function SyllabusSummary({
  activity,
  headerRef,
  showExpanded,
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  headerRef?: React.RefObject<HTMLElement>;
  showExpanded?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\SyllabusSummary",
  );

  const syllabus = activity.syllabus;
  const hasSyllabus =
    !activity.isOneTime &&
    !activity.is_ongoing_weekly &&
    !!syllabus &&
    !!syllabus?.published_at &&
    !!syllabus?.lessons &&
    syllabus?.lessons?.length > 0;

  if (!hasSyllabus) {
    return null;
  }

  return (
    <Box
      sx={(_theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
      })}
    >
      <Syllabus
        activity={activity as ActivityWithSyllabus}
        headerRef={headerRef}
        isModal={showExpanded}
      />
      {!showExpanded && (
        <ShowMoreModal
          buttonLabel={t("Show lesson", {
            length: syllabus.lessons.length,
            count: syllabus.lessons.length,
            defaultValue_plural: "Show all {{length}} lessons",
          })}
          trackingName={"syllabus_summary"}
        >
          <Syllabus activity={activity as ActivityWithSyllabus} isModal />
        </ShowMoreModal>
      )}
    </Box>
  );
}

function Syllabus({
  activity,
  isModal = false,
  headerRef,
}: {
  activity: ActivityWithSyllabus;
  isModal?: boolean;
  headerRef?: React.RefObject<HTMLElement>;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\SyllabusSummary",
  );
  const curriculum = getCurriculumValue(activity.attributes);
  const standard = getStandardValue(activity.attributes);

  const lessons = activity.syllabus.lessons;
  const hasUnits = lessons[0].unit != null;
  const numUnits = lessons[lessons.length - 1].unit?.unitNumber;
  const numWeeks = lessons[lessons.length - 1].week_number + 1;

  // Only need to show all the lessons in the modal
  const filteredLessons = isModal ? lessons : lessons.slice(0, 4);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflowX: "hidden",
      }}
    >
      <Box ref={headerRef}>
        {!isModal && <HeadingMarker />}
        <Typography emphasized variant="h4" id="syllabus">
          {t("Syllabus")}
        </Typography>
      </Box>
      {(curriculum || standard) && (
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            gap: isModal ? 8 : 32,
            flexDirection: isModal ? "column" : "row",
            paddingY: 8,
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
            },
          })}
        >
          {curriculum && <Curriculum curriculum={curriculum} />}
          {standard && <Standard standard={standard} />}
        </Box>
      )}
      <Box>
        <Box
          sx={(theme: Theme) =>
            isModal
              ? {}
              : {
                  height: 400,
                  overflow: "hidden",
                  position: "relative",
                  [theme.breakpoints.down("md")]: {
                    height: 480,
                  },
                }
          }
        >
          <Box
            sx={{
              display: "flex",
              paddingY: 8,
              gap: 12,
              alignItems: "baseline",
            }}
          >
            {hasUnits && (
              <Typography emphasized variant="h6" color="secondary.700">
                {t("{{numUnits}} Unit", {
                  numUnits: numUnits,
                  count: numUnits ?? 0,
                  defaultValue_plural: "{{numUnits}} Units",
                })}
              </Typography>
            )}
            <Typography emphasized variant="h6" color="info.700">
              {t("{{numLessons}} Lesson", {
                numLessons: lessons.length,
                count: lessons.length,
                defaultValue_plural: "{{numLessons}} Lessons",
              })}
            </Typography>
            <Typography variant="body2" color="grey.600">
              {t("over {{numWeeks}} Week", {
                numWeeks,
                count: numWeeks,
                defaultValue_plural: "over {{numWeeks}} Weeks",
              })}
            </Typography>
          </Box>

          {filteredLessons.map((lesson, i, lessons) => {
            const isStartOfUnit =
              (hasUnits && i == 0) ||
              lesson.unit?.uid != lessons[i - 1]?.unit?.uid;
            return (
              <Box
                key={lesson.uid}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  "&:not(:last-child)": {
                    borderBottom: 1,
                    borderColor: "grey.200",
                  },
                }}
              >
                <Lesson lesson={lesson} activity={activity}>
                  {isStartOfUnit && (
                    <Box sx={{ paddingY: 4 }}>
                      <Typography
                        emphasized
                        variant="subtitle1"
                        color="secondary.700"
                      >
                        {t("Unit {{number}}: {{title}}", {
                          title: lesson.unit?.title,
                          number: lesson.unit?.unitNumber,
                        })}
                      </Typography>
                    </Box>
                  )}
                </Lesson>
              </Box>
            );
          })}
          {!isModal && (
            <Box
              sx={{
                height: 48,
                width: "100%",
                position: "absolute",
                bottom: -3.33,
                background:
                  "linear-gradient(rgba(255, 255, 255, 0) 20%, rgb(255, 255, 255) 100%)",
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

type LessonType = {
  uid: string;
  title: string | null;
  description: string | null;
  week_number: number;
  order_within_week: number;
  lessonNumber: number | null;
  videoDurationSeconds: number;
  assignmentsCount?: number | undefined;
  unit: {
    __typename?: "Unit";
    uid: string;
    title: string | null;
    unitNumber: number | null;
  } | null;
};

function Lesson({
  lesson,
  activity,
  children,
}: {
  lesson: LessonType;
  activity: ClassDetailsQuery_ActivityFragment;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\SyllabusSummary",
  );

  const duration = lesson.videoDurationSeconds;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginTop: 16,
        marginBottom: 24,
      }}
    >
      {children}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "baseline",
          gap: 12,
          paddingY: 4,
        }}
      >
        <Typography
          emphasized
          variant="subtitle1"
          color="info.700"
          sx={{ flexShrink: 0 }}
        >
          {t("Lesson ") + lesson.lessonNumber + ":"}
        </Typography>
        <Typography variant="subtitle1" color="info.700" sx={{ flexShrink: 1 }}>
          {" "}
          {lesson.title}{" "}
        </Typography>
      </Box>
      {lesson.description && (
        <Box component={"pre"} sx={{ margin: 0, whiteSpace: "pre-line" }}>
          <Typography variant="body1"> {lesson.description.trim()} </Typography>
        </Box>
      )}
      <>
        {!activity.isSelfPaced && (
          <Box sx={{ display: "flex", mr: 16, gap: 4, alignItems: "center" }}>
            <Icon icon={faSignalStream} />
            <Typography variant="subtitle2">
              {" "}
              {t("{{minutes}} mins online live lesson ", {
                minutes: activity.duration_minutes,
              })}
            </Typography>
          </Box>
        )}
        {lesson.assignmentsCount != 0 && (
          <Box sx={{ display: "flex", mr: 16, gap: 4, alignItems: "center" }}>
            <Icon icon={faRectangleList} />
            <Typography variant="subtitle2">
              {" "}
              {t("{{assignments}} assignment", {
                assignments: lesson.assignmentsCount,
                count: lesson.assignmentsCount,
                defaultValue_plural: "{{assignments}} assignments",
              })}{" "}
            </Typography>
          </Box>
        )}
        {activity.isSelfPaced && duration >= 300 && (
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Icon icon={faCirclePlay} />
            <Typography variant="subtitle2">
              {t("{{duration}} mins of video lessons", {
                duration: Math.round(duration / 60),
              })}
            </Typography>
          </Box>
        )}
      </>
    </Box>
  );
}

function Curriculum({ curriculum }: { curriculum: string }) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\SyllabusSummary",
  );

  return (
    <Box sx={{ display: "flex", gap: 8 }}>
      <YellowBlobIcon icon={faBooks} />
      <Box>
        <Typography variant="subtitle1">{t("Curriculum")}</Typography>
        <Typography variant="body2" color="grey.600">
          {t("Follows {{curriculum}} Curriculum", { curriculum })}
        </Typography>
      </Box>
    </Box>
  );
}

function Standard({ standard }: { standard: string }) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\SyllabusSummary",
  );

  const StandardDescription: Record<string, string> = {
    [ActivityStandard.CCSS]: t(
      "Standards for Math and English Language Arts designed to ensure college and career readiness.",
    ),
    [ActivityStandard.NGSS]: t(
      "Science standards fostering inquiry and cross-disciplinary knowledge.",
    ),
    [ActivityStandard.NCSS]: t(
      "Guidelines promoting civic competence and understanding of social dynamics.",
    ),
    [ActivityStandard.AP]: t(
      "College-level courses offering credit and advanced study opportunities.",
    ),
    [ActivityStandard.IB]: t(
      "Rigorous global education standards emphasizing critical thinking.",
    ),
  };
  const tooltip = StandardDescription[standard];

  return (
    <Box sx={{ display: "flex", gap: 8 }}>
      <YellowBlobIcon icon={faGraduationCap} />
      <Box>
        <Typography variant="subtitle1">{t("Standards")}</Typography>
        <Typography variant="body2" color="grey.600">
          {t("Aligned with {{standard}}", { standard })}
        </Typography>
        {tooltip && (
          <Tooltip placement="top" title={tooltip}>
            <Icon
              icon={faQuestionCircle}
              sx={{
                color: "primary.800",
                marginLeft: 4,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
